import SysColumn from "app/class/sys-column";
import SysTable from "app/class/sys-table";
import { getCodeEditorProfile } from "./profiles";
import {
    CodeEditorProfile,
    CodeEditorProfileId,
    CodeEditorResolveContext,
    CodeEditorTableSchema,
} from "./types";

const asBoolean = (value: unknown) =>
    value === true ||
    value === "true" ||
    value === "1" ||
    value === 1;

const resolveTargetTable = (
    values: Record<string, unknown>,
    allTables: SysTable[]
) => {
    const tableRef = values.sys_table_id || values.for_table || values.target_table;

    if (!tableRef) {
        return null;
    }

    return allTables.find((table) => table.id === tableRef || table.name === tableRef) ?? null;
};

const columnsForTable = (table: SysTable | null, allColumns: SysColumn[]) => {
    if (!table) {
        return [];
    }

    return allColumns
        .filter((candidate) => candidate.sys_table_id === table.id)
        .sort((first, second) => first.order - second.order);
};

const buildTableSchemas = (
    allTables: SysTable[],
    allColumns: SysColumn[]
): CodeEditorTableSchema[] =>
    allTables
        .map((candidate) => ({
            id: candidate.id,
            name: candidate.name,
            label: candidate.label,
            columns: columnsForTable(candidate, allColumns),
        }))
        .filter((schema) => schema.name);

const resolveFormActionProfileId = (values: Record<string, unknown>): CodeEditorProfileId => {
    const isListAction = asBoolean(values.is_list_action);
    const isRelatedAction = asBoolean(values.is_related_action);

    if (isListAction && isRelatedAction) {
        return "form_action.related_list";
    }

    if (isListAction) {
        return "form_action.list";
    }

    if (isRelatedAction) {
        return "form_action.related_form";
    }

    return "form_action.form";
};

const resolveClientScriptProfileId = (values: Record<string, unknown>): CodeEditorProfileId => {
    if (values.event_type === "onSubmit") {
        return "client_script.onsubmit";
    }

    if (values.event_type === "onChange") {
        return "client_script.onchange";
    }

    return "client_script.onload";
};

export const resolveCodeEditorProfile = ({
    table,
    column,
    values,
    allTables,
    allColumns,
    profileId: explicitProfileId,
}: CodeEditorResolveContext): CodeEditorProfile | null => {
    let profileId: CodeEditorProfileId | null = explicitProfileId || null;

    if (!profileId) {
        if (!table || !column || column.column_type !== "CODE" || column.column_subtype !== "JAVASCRIPT") {
            return null;
        }

    if (table.name === "sys_business_rules" && column.name === "condition_script") {
        profileId = "business_rule.condition";
    }

    if (table.name === "sys_business_rules" && column.name === "script") {
        profileId = "business_rule.action";
    }

    if (table.name === "sys_jobs" && column.name === "condition_script") {
        profileId = "platform_job.condition";
    }

    if (table.name === "sys_jobs" && column.name === "script") {
        profileId = "platform_job.action";
    }

    if (table.name === "sys_form_action" && column.name === "script") {
        profileId = resolveFormActionProfileId(values);
    }

    if (table.name === "sys_columns" && column.name === "default_value_script") {
        profileId = "default_value.script";
    }

    if (table.name === "sys_client_script" && column.name === "script") {
        profileId = resolveClientScriptProfileId(values);
    }

    if (table.name === "sys_api_scripts" && column.name === "code") {
        profileId = "dynamic_api.handler";
    }

    if (table.name === "sys_gate_pages" && column.name === "script_code") {
        profileId = "gate.page.component";
    }

    if (table.name === "sys_gate_widgets" && column.name === "script_code") {
        profileId = "gate.widget.component";
    }

    if (table.name === "sys_sp_form_catalog" && column.name === "script_code") {
        profileId = "service_portal.form_script";
    }

    }

    if (!profileId) {
        return null;
    }

    const targetTable = resolveTargetTable(values, allTables);
    const targetColumns = columnsForTable(targetTable, allColumns);
    const tableSchemas = buildTableSchemas(allTables, allColumns);

    return {
        ...getCodeEditorProfile(profileId),
        targetTable: targetTable
            ? {
                id: targetTable.id,
                name: targetTable.name,
                label: targetTable.label,
                columns: targetColumns,
            }
            : null,
        targetColumns,
        tableSchemas,
    };
};
